import MprcLoadable from "components/MprcLoadable";
import { CalendarFilterBy, ManageEnrollOperation, PublicationType, SupervisorTrainingsQueryMode, TrainingType } from "generated/CoreApi_v1";
import { BlankLayout } from "layouts/BlankLayout";
import { LmsLayout } from "layouts/LmsLayout";
import { WelcomeLayout } from "layouts/WelcomeLayout";
import React from "react";
import { Navigate, Route, Routes } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { RootPaths } from "routes/RootRouter";
import { ListUsersManagerEfficacy } from "views/assessment/managerEfficacy/ListUsersManagerEfficacy";
import { ListTopicChallenge } from "views/assessment/topicChallenge/Home/ListTopicChallenge/ListTopicChallenge";
import { NewKnowledgeTest } from "views/assessment/topicChallenge/NewKnowledgeTest";
import { ListClassrooms } from "views/lms/classroom/Home/ListClassrooms";
import { DevPlanHome } from "views/lms/devPlan/Home";
import DevPlanManagementWrapper from "views/lms/devPlan/Home/components/DevPlanManagementWrapper";
import { EditDevPlanView, OperationMode } from "views/lms/devPlan/Home/Edit/EditDevPlanView/EditDevPlanView";
import { UserDevPlans } from "views/lms/devPlan/Home/List/UserDevPlans";
import { EnrollmentManagementWrapper } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";
import { EnrollmentManager } from "views/lms/enrollment/Home/EnrollmentManager";
import { LmsHome } from "views/lms/home";
import { CheckOnlineMeetingUrl } from "views/lms/instructor/Home/CheckOnlineMeetingUrl";
import { MeetingAttendanceList } from "views/lms/meeting/views/MeetingAttendanceList";
import { MeetingQrCode } from "views/lms/meeting/views/MeetingQrCode";
import { LmsCarouselTrainingAttending } from "views/lms/student/Carousel/LmsCarouselTrainingAttending";
import { LmsCarouselTrainingMandatory } from "views/lms/student/Carousel/LmsCarouselTrainingMandatory";
import { LmsCatalog } from "views/lms/student/Catalog";
import { EnrollmentView } from "views/lms/student/EnrollmentView";
import { TrainingPostViewer } from "views/lms/student/Publication/TrainingPostViewer";
import { StudentHistory } from "views/lms/student/StudentHistory";
import { StudentLibrary } from "views/lms/student/StudentLibrary";
import { StudentMeetings } from "views/lms/student/StudentMeetings";
import { TrainingView } from "views/lms/student/TrainingView";

const TrainingPanelSupervisorDashboard = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'dashboard' */"views/dashboard/TrainingPanel/TrainingPanelSupervisorDashboard")));
const TrainingPanelDashboard = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'dashboard' */"views/dashboard/TrainingPanel/TrainingPanelDashboard")));
const TrainingIndexDashboard = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'dashboard' */"views/dashboard/TrainingIndex")));

const TrainingPreview = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/TrainingPreview")));
const LmsApprovals = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Approvals")));
const RegisterActivityMatrix = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/RegisterActivityMatrix/RegisterActivityMatrix")));
const RegisterMeeting = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/RegisterMeeting/RegisterMeeting")));
const RegisterMeetingMatrix = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/RegisterMeetingMatrix/RegisterMeetingMatrix")));
const InstructorMaterialList = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/MaterialList/InstructorMaterialList")));
const SupervisorRegisterActivity = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/RegisterActivity/SupervisorRegisterActivity")));
const SupervisorTrainings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor")));
const SupervisorClassroom = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/SupervisorClassroom")));
const SupervisorClassroomNotices = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/SupervisorClassroomNotices")));
const SupervisorClassroomRules = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/SupervisorClassroomRules")));
const SupervisorClassroomSchedule = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/SupervisorClassroomSchedule")));
const SupervisorWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsMonitoring' */"views/lms/monitoring/Supervisor/SupervisorWrapper")));

const ListAssDeploy = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/assDeploy/Home/ListAssDeploy")));
const EditEssayCorrection = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/evaluationCorrection/Home/EditEssayCorrection/EditEssayCorrection")));
const EvaluationCorrectionWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/evaluationCorrection/Home/EvaluationCorrectionWrapper")));
const SurveyManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/survey/Home/SurveyManagementWrapper")));
const ListEssayCorrection = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/evaluationCorrection/Home/ListEssayCorrection/ListEssayCorrection")));
const ListSurveys = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/survey/Home/ListSurveys")));
const ListEvaluations = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/evaluation/Home/ListEvaluations")));
const ListQuestionGroups = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/questionBank/Home/ListQuestionGroup")));
const EvaluationManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/evaluation/Home/EvaluationManagementWrapper")));
const QuestionGroupManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsAssessment' */"views/assessment/questionBank/Home/QuestionBankManagementWrapper")));

const InstructorManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsInstructor' */"views/lms/instructor/Home/InstructorManagementWrapper")));
const ListInstructorClasses = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsInstructor' */"views/lms/instructor/Home/ListInstructorClasses")));
const ListInstructorMeetings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsInstructor' */"views/lms/instructor/Home/ListInstructorMeetings")));
const ListInstructors = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsInstructor' */"views/lms/instructor/Home/ListInstructors")));

const TrainingManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/training/Home/TrainingManagementWrapper")));
const ListTrainings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/training/Home/ListTrainings")));
const EditTraining = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/training/Home/EditTraining")));
const ClassroomManagementWrapper = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/classroom/Home/ClassroomManagementWrapper")));
const EditClassroom = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/classroom/Home/EditClassroom")));
const GameMapExample = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/training/Home/EditTraining/EditLearnPath/GameMapExample")));
const DebugScormTrainings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsManagement' */"views/lms/training/Scorm")));

const LmsSettingsHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings")));
const EditCarouselSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/Carousel")));
const EditIndexTrainingSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/IndexTraining")));
const EditKnowledgeTestSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/KnowledgeTest")));
const ListMeetingRoom = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/MeetingRoom")));
const EditReminderSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/Reminder")));
const EditLmsSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/Setting/EditLmsSettings")));
const TrainingSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/TrainingDefaults")));
const EditLmsAward = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'lmsConfig' */"views/lms/settings/Home/Award/EditLmsAward")));

class Paths {
    static readonly LmsHome = "Home";
    static readonly StudentLibrary = "Library";
    static readonly AllMeetings = "Meetings";
    static readonly StudentMeetings = "StudentMeetings";
    static readonly InstructorMeetings = "InstructorMeetings";
    static readonly InstructorCheckOnlineMeeting = "InstructorCheckOnlineMeeting";

    static readonly AllDevPlans = "AllDevPlans";
    static readonly MyDevPlans = "MyPlans";
    static readonly TeamDevPlans = "TeamPlans";
    static readonly UserDevPlans = "UserPlans";
    static readonly DevPlanView = "DevPlanView";

    static readonly StudentHistory = "History";
    static readonly StudentCatalog = "Catalog";

    static readonly NewKnowledgeTest = "NewKnowledgeTest";
    static readonly KnowledgeTestProgress = "KnowledgeProgress";

    static readonly ManagerEfficacy = "ManagerEfficacy";

    static readonly EvaluationCorrection = "EvaluationCorrection";
    static readonly Approvals = "Approvals";
    static readonly PreApprovals = "PreApprovals";
    static readonly ConclusionForm = "ConclusionForm";
    static readonly Supervisor = "Supervisor";
    static readonly SupervisorTrainingPanel = "SupervisorTrainingPanel";
    static readonly TrainingPanel = "TrainingPanel";
    static readonly TrainingIndex = "TrainingIndex";
    static readonly Instructor = "Instructor";
    static readonly Coordinator = "Coordinator";

    static readonly Management = "Management";
    static readonly TrainingList = "Trainings";
    static readonly InstructorAllocation = "InstructorAllocation";
    static readonly WaitingList = "WaitingList";
    static readonly EnrollmentManager = "EnrollmentManager";
    static readonly QuestionsBank = "QuestionsBank";
    static readonly EvaluationsBank = "EvaluationsBank";
    static readonly SurveysBank = "SurveysBank";
    static readonly TopicsChallenge = "TopicsChallenge";
    static readonly ApplySurveys = "ApplySurveys";
    static readonly MaterialList = "MaterialList";

    static readonly Settings = "Settings";
    static readonly SettingsIndexTraining = "SettingsIndexTraining";
    static readonly SettingsReminder = "SettingsReminder";
    static readonly SettingsConfiguration = "SettingsConfiguration";
    static readonly SettingsCancelEnrollReasons = "SettingsCancelEnrollReasons";
    static readonly SettingsEventRoomList = "SettingsEventRoomList";
    static readonly SettingsKnowledgeTest = "SettingsKnowledgeTest";
    static readonly SettingsCarousel = "SettingsCarousel";
    static readonly SettingsTrainingDefaults = "SettingsTrainingDefaults";
    static readonly SettingsAward = "SettingsAward";
    static readonly SettingsBadge = "Badge";

    static readonly EnrollmentViewer = "EnrollmentViewer";
    static readonly TrainingViewer = "TrainingViewer";
    static readonly TrainingPreview = "TrainingPreview";

    static readonly TrainingPostPublicationViewer = "Post";

    static readonly CarouselTrainingAttending = "TrainingAttending";
    static readonly CarouselTrainingMandatory = "TrainingMandatory";

    static readonly LmsEventRegistration = "LmsEventRegistration";

    static readonly DebugScormTrainings = "DebugScormTrainings";
}

export class LmsPaths {
    static readonly Prefix = "/Lms";

    static readonly Home = LmsPaths.Prefix;

    static readonly StudentLibrary = LmsPaths.Prefix + "/" + Paths.StudentLibrary;

    static readonly AllMeetings = LmsPaths.Prefix + "/" + Paths.AllMeetings;
    static readonly StudentMeetings = LmsPaths.Prefix + "/" + Paths.StudentMeetings;

    static readonly AllDevPlans = LmsPaths.Prefix + "/" + Paths.AllDevPlans;
    static readonly TeamDevPlans = LmsPaths.Prefix + "/" + Paths.TeamDevPlans;
    static readonly MyDevPlans = LmsPaths.Prefix + "/" + Paths.MyDevPlans;
    static readonly UserDevPlans = LmsPaths.Prefix + "/" + Paths.UserDevPlans;

    static readonly DevPlanView = LmsPaths.Prefix + "/" + Paths.DevPlanView;

    static readonly InstructorMeetings = LmsPaths.Prefix + "/" + Paths.InstructorMeetings;
    static readonly InstructorCheckOnlineMeeting = LmsPaths.Prefix + "/" + Paths.InstructorCheckOnlineMeeting;

    static readonly StudentHistory = LmsPaths.Prefix + "/" + Paths.StudentHistory;
    static readonly StudentCatalog = LmsPaths.Prefix + "/" + Paths.StudentCatalog;

    static readonly NewKnowledgeTest = LmsPaths.Prefix + "/" + Paths.NewKnowledgeTest;
    static readonly KnowledgeTestProgress = LmsPaths.Prefix + "/" + Paths.KnowledgeTestProgress;

    static readonly ManagerEfficacy = LmsPaths.Prefix + "/" + Paths.ManagerEfficacy;

    static readonly Supervisor = LmsPaths.Prefix + "/" + Paths.Supervisor;
    static readonly SupervisorInstructor = LmsPaths.Prefix + "/" + Paths.Supervisor + "/" + Paths.Instructor;
    static readonly SupervisorCoordinator = LmsPaths.Prefix + "/" + Paths.Supervisor + "/" + Paths.Coordinator;
    static readonly SupervisorTrainingPanel = LmsPaths.Prefix + "/" + Paths.SupervisorTrainingPanel;

    static readonly TrainingPanel = LmsPaths.Prefix + "/" + Paths.TrainingPanel;
    static readonly TrainingIndex = LmsPaths.Prefix + "/" + Paths.TrainingIndex;

    static readonly Approvals = LmsPaths.Prefix + "/" + Paths.Approvals;
    static readonly ApprovalsPreApproval = LmsPaths.Prefix + "/" + Paths.Approvals + "/" + Paths.PreApprovals;
    static readonly ApprovalsConclusionForm = LmsPaths.Prefix + "/" + Paths.Approvals + "/" + Paths.ConclusionForm;

    static readonly EvaluationCorrection = LmsPaths.Prefix + "/" + Paths.EvaluationCorrection;

    static readonly TrainingList = LmsPaths.Prefix + "/" + Paths.TrainingList;
    static readonly MaterialList = LmsPaths.Prefix + "/" + Paths.MaterialList;

    static readonly InstructorAllocation = LmsPaths.Prefix + "/" + Paths.InstructorAllocation;
    static readonly WaitingList = LmsPaths.Prefix + "/" + Paths.WaitingList;
    static readonly EnrollmentManager = LmsPaths.Prefix + "/" + Paths.EnrollmentManager;

    static readonly QuestionsBank = LmsPaths.Prefix + "/" + Paths.QuestionsBank;
    static readonly EvaluationsBank = LmsPaths.Prefix + "/" + Paths.EvaluationsBank;
    static readonly SurveysBank = LmsPaths.Prefix + "/" + Paths.SurveysBank;

    static readonly TopicsChallenge = LmsPaths.Prefix + "/" + Paths.TopicsChallenge;
    static readonly ApplySurveys = LmsPaths.Prefix + "/" + Paths.ApplySurveys;

    static readonly DebugScormTrainings = LmsPaths.Prefix + "/" + Paths.DebugScormTrainings;

    static readonly Settings = LmsPaths.Prefix + "/" + Paths.Settings;
    static readonly SettingsIndexTraining = LmsPaths.Prefix + "/" + Paths.SettingsIndexTraining;
    static readonly SettingsConfiguration = LmsPaths.Prefix + "/" + Paths.SettingsConfiguration;
    static readonly SettingsCancelEnrollReasons = LmsPaths.Prefix + "/" + Paths.SettingsCancelEnrollReasons;
    static readonly SettingsEventRoomList = LmsPaths.Prefix + "/" + Paths.SettingsEventRoomList;
    static readonly SettingsKnowledgeTest = LmsPaths.Prefix + "/" + Paths.SettingsKnowledgeTest;
    static readonly SettingsReminder = LmsPaths.Prefix + "/" + Paths.SettingsReminder;
    static readonly SettingsCarousel = LmsPaths.Prefix + "/" + Paths.SettingsCarousel;
    static readonly SettingsTrainingDefaults = LmsPaths.Prefix + "/" + Paths.SettingsTrainingDefaults;
    static readonly SettingsAward = LmsPaths.Prefix + "/" + Paths.SettingsAward;
    static readonly SettingsBadge = LmsPaths.Prefix + "/" + Paths.SettingsBadge;

    static readonly TrainingViewer = LmsPaths.Prefix + "/" + Paths.TrainingViewer;
    static readonly EnrollmentViewer = LmsPaths.Prefix + "/" + Paths.EnrollmentViewer;
    static readonly TrainingPostPublicationViewer = LmsPaths.Prefix + "/" + Paths.TrainingPostPublicationViewer;
    static readonly TrainingPreview = LmsPaths.Prefix + "/" + Paths.TrainingPreview;
    static readonly CarouselTrainingAttending = LmsPaths.Prefix + "/" + Paths.CarouselTrainingAttending;
    static readonly CarouselTrainingMandatory = LmsPaths.Prefix + "/" + Paths.CarouselTrainingMandatory;

    static readonly LmsEventRegistration = Paths.LmsEventRegistration;


    static BuildEditTraining(id: string | null, trainingType: TrainingType | string) { return `${LmsPaths.TrainingList}/${(typeof (trainingType) == "string" ? trainingType : TrainingType[trainingType ?? TrainingType.Course])}` + (id ? `/${id}` : "/New"); }
    static BuildListClassroom(trainingId: string, trainingType: TrainingType | string) { return `${this.BuildEditTraining(trainingId, trainingType)}/Classrooms`; }
    static BuildEditClassroom(trainingId: string, trainingType: TrainingType | string, classroomId: string | null) { return this.BuildListClassroom(trainingId, trainingType) + (classroomId ? `/${classroomId}` : "/New"); }
    static BuildMaterialList(classroomId?: string) { return `${LmsPaths.MaterialList}/${classroomId}`; }

    static BuildEnrollmentViewer(trainingType: TrainingType | string, id?: string, componentId?: string) { return `${LmsPaths.EnrollmentViewer}/${(typeof (trainingType) == "string" ? trainingType : TrainingType[trainingType ?? TrainingType.Course])}` + (id ? `/${id}` : "") + (componentId ? `?c=${componentId}` : ""); }
    static BuildTrainingViewer(id: string) { return `${LmsPaths.TrainingViewer}/${id}`; }
    static BuildEnrollmentPreview(trainingId: string, classroomId?: string) { return `${LmsPaths.TrainingPreview}/${trainingId}${(classroomId == null ? "" : "/" + classroomId)}`; }

    static BuildListInstructorClasses(instructorId: string) { return `${LmsPaths.InstructorAllocation}/${instructorId}/Classrooms`; }
    static BuildListInstructorMeetings(instructorId: string) { return `${LmsPaths.InstructorAllocation}/${instructorId}/Meetings`; }

    static BuildSupervisorClassroom(classroomId: string) { return `${LmsPaths.Supervisor}/${classroomId}`; }
    static BuildSupervisorClassroomNotices(classroomId: string) { return `${LmsPaths.Supervisor}/${classroomId}/Notices`; }
    static BuildSupervisorClassroomSchedule(classroomId: string) { return `${LmsPaths.Supervisor}/${classroomId}/ClassroomSchedule`; }
    static BuildSupervisorMeetingAttendanceList(classroomId: string, componentId: string) { return `${LmsPaths.Supervisor}/${classroomId}/MeetingAttendanceList/${componentId}`; }
    static BuildSupervisorMeetingQrCode(classroomId: string, componentId: string) { return `${LmsPaths.Supervisor}/${classroomId}/MeetingQrCode/${componentId}`; }
    static BuildSupervisorClassroomRules(classroomId: string, trainingType: TrainingType | string) { return `${LmsPaths.Supervisor}/${classroomId}/${(typeof (trainingType) == "string" ? trainingType : TrainingType[trainingType ?? TrainingType.Course])}/Rules`; }
    static BuildSupervisorRegisterMeeting(classroomId: string, componentId: string) { return `${LmsPaths.Supervisor}/${classroomId}/Meeting/${componentId}`; }
    static BuildSupervisorRegisterMeetingMatrix(classroomId: string) { return `${LmsPaths.Supervisor}/${classroomId}/MeetingMatrix`; }
    static BuildSupervisorRegisterActivity(classroomId: string, componentId: string) { return `${LmsPaths.Supervisor}/${classroomId}/Activity/${componentId}`; }
    static BuildSupervisorRegisterActivityMatrix(classroomId: string) { return `${LmsPaths.Supervisor}/${classroomId}/ActivityMatrix`; }

    static BuildEditEssayCorrection(assessmentId: string, isHome: boolean) { return `${LmsPaths.EvaluationCorrection}${isHome ? "/Home" : ""}/${assessmentId}`; }
    static BuildListEssayCorrection(classroomId?: string, isHome?: boolean) { return `${LmsPaths.EvaluationCorrection}${isHome ? "/Home" : ""}${classroomId ? '/Classroom/' + classroomId : ''}`; }

    static BuildDevPlanView(devplanId: string, operation: OperationMode) {
        if (operation == OperationMode.View) {
            return `${LmsPaths.DevPlanView}/${devplanId}`;
        } else {
            return `${LmsPaths.DevPlanView}/${devplanId}/Edit`;
        }
    }

    static BuildEnrollmentManager(trainingId?: string, classroomId?: string, operation?: ManageEnrollOperation) {
        var parts = [];
        if (trainingId) {
            parts.push(`/${trainingId}`);
            if (operation) parts.push(`/${ManageEnrollOperation[operation]}`);
        }

        const q = classroomId ? "?c=" + classroomId : "";
        const url = `${LmsPaths.EnrollmentManager}${parts.join("")}` + q;
        return url;
    }

    static BuildEditBadge(id: string | null) {
        return `${LmsPaths.SettingsBadge}/${id ? `${id}` : "New"}`;
    }

    /**
     * Obtem a URL para visualização da publicação .
     * @param publicationId
     * @param type
     * @returns
     */
    static PublicationViewerResolver(publicationId: string, type: PublicationType): string {
        switch (type) {
            case PublicationType.TrainingPost:
                return LmsPaths.TrainingPostPublicationViewer + '/' + publicationId;
            default:
                return KnowledgePaths.PublicationViewerResolver(publicationId, type);
        }
    }
}

export const LmsRouter = () => {
    return (
        <Routes>
            <Route element={<LmsLayout />} >
                <Route path="" element={<LmsHome />} />
                <Route path={Paths.LmsHome} element={<LmsHome />} />
                <Route path={Paths.StudentCatalog} element={<LmsCatalog />} />
                <Route path={Paths.StudentHistory} element={<StudentHistory />} />
                <Route path={Paths.StudentLibrary} element={<StudentLibrary />} />

                <Route path={Paths.AllMeetings} element={<StudentMeetings filterBy={CalendarFilterBy.All} />} />
                <Route path={Paths.StudentMeetings} element={<StudentMeetings filterBy={CalendarFilterBy.Participant} />} />
                <Route path={Paths.InstructorMeetings} element={<StudentMeetings filterBy={CalendarFilterBy.Instructor} />} />

                <Route path={Paths.DebugScormTrainings} element={<DebugScormTrainings />} />

                <Route path={Paths.InstructorCheckOnlineMeeting} element={<CheckOnlineMeetingUrl />} />

                <Route path={Paths.TrainingList} element={<TrainingManagementWrapper />}>
                    <Route index element={<ListTrainings />} />
                    <Route path=":trainingType">
                        <Route path="New" element={<EditTraining />} />
                        <Route path=":trainingId" >
                            <Route index element={<EditTraining />} />

                            <Route path="Classrooms" element={<ClassroomManagementWrapper />}>
                                <Route index element={<ListClassrooms />} />
                                <Route path="New" element={<EditClassroom />} />
                                <Route path=":classroomId">
                                    <Route index element={<EditClassroom />} />
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                </Route>

                <Route path={Paths.QuestionsBank} element={<QuestionGroupManagementWrapper />}>
                    <Route index element={<ListQuestionGroups />} />
                </Route>

                <Route path={Paths.EvaluationsBank} element={<EvaluationManagementWrapper />}>
                    <Route index element={<ListEvaluations />} />
                </Route>

                <Route path={Paths.SurveysBank} element={<SurveyManagementWrapper />}>
                    <Route index element={<ListSurveys />} />
                </Route>

                <Route path={Paths.ApplySurveys}>
                    <Route index element={<ListAssDeploy />} />
                </Route>

                <Route path={Paths.TopicsChallenge}>
                    <Route index element={<ListTopicChallenge />} />
                </Route>

                <Route path={Paths.NewKnowledgeTest}>
                    <Route index element={<NewKnowledgeTest />} />
                </Route>

                <Route path={Paths.ManagerEfficacy}>
                    <Route index element={<ListUsersManagerEfficacy />} />
                </Route>

                <Route path={Paths.EnrollmentManager} element={<EnrollmentManagementWrapper />}>
                    <Route index element={<EnrollmentManager />} />
                    <Route path=":trainingId">
                        <Route index element={<EnrollmentManager />} />
                    </Route>
                    <Route path=":trainingId/:operationId">
                        <Route index element={<EnrollmentManager />} />
                    </Route>
                </Route>

                <Route path={Paths.InstructorAllocation} element={<InstructorManagementWrapper />}>
                    <Route index element={<ListInstructors />} />
                    <Route path=":instructorId/Classrooms">
                        <Route index element={<ListInstructorClasses />} />
                    </Route>
                    <Route path=":instructorId/Meetings">
                        <Route index element={<ListInstructorMeetings />} />
                    </Route>
                </Route>

                <Route path={Paths.EvaluationCorrection} element={<EvaluationCorrectionWrapper />}>
                    <Route path="Home">
                        <Route index element={<ListEssayCorrection isHome />} />
                        <Route path="Classroom/:classroomId">
                            <Route index element={<ListEssayCorrection isHome />} />
                        </Route>
                        <Route path=":assessmentId">
                            <Route index element={<EditEssayCorrection isHome />} />
                        </Route>
                    </Route>
                    <Route index element={<ListEssayCorrection />} />
                    <Route path="Classroom/:classroomId">
                        <Route index element={<ListEssayCorrection />} />
                    </Route>
                    <Route path=":assessmentId">
                        <Route index element={<EditEssayCorrection />} />
                    </Route>
                </Route>

                <Route path={Paths.MaterialList}>
                    <Route path=":classroomId">
                        <Route index element={<InstructorMaterialList />} />
                    </Route>
                </Route>

                <Route path={"gamemap"} element={<GameMapExample />} />

                <Route path={Paths.TrainingPostPublicationViewer} >
                    <Route path=":id" element={<TrainingPostViewer />} />
                </Route>

                <Route element={<DevPlanManagementWrapper />}>
                    <Route path={Paths.MyDevPlans} element={<DevPlanHome selectedOption={"personal"} />} />
                    <Route path={Paths.AllDevPlans} element={<DevPlanHome selectedOption={"all"} />} />
                    <Route path={Paths.TeamDevPlans} element={<DevPlanHome selectedOption={"team"} />} />
                    <Route path={Paths.DevPlanView}>
                        <Route path=":devPlanId" element={<EditDevPlanView mode={OperationMode.View} />} />
                        <Route path=":devPlanId/Edit" element={<EditDevPlanView mode={OperationMode.Edit} />} />
                    </Route>
                    <Route path={Paths.UserDevPlans}>
                        <Route path=":userId" element={<UserDevPlans />} />
                    </Route>
                </Route>

                <Route path={Paths.Settings} element={<LmsSettingsHome />} />
                <Route path={Paths.SettingsIndexTraining} element={<EditIndexTrainingSettings />} />
                <Route path={Paths.SettingsConfiguration} element={<EditLmsSettings />} />
                <Route path={Paths.SettingsEventRoomList} element={<ListMeetingRoom />} />
                <Route path={Paths.SettingsReminder} element={<EditReminderSettings />} />
                <Route path={Paths.SettingsKnowledgeTest} element={<EditKnowledgeTestSettings />} />
                <Route path={Paths.SettingsCarousel} element={<EditCarouselSettings />} />
                <Route path={Paths.SettingsTrainingDefaults} element={<TrainingSettings />} />

                <Route path={Paths.SettingsAward} element={<EditLmsAward tabName="award" />} />
                <Route path={Paths.SettingsBadge} element={<EditLmsAward tabName="badge" />} />

                <Route path={Paths.SupervisorTrainingPanel} element={<TrainingPanelSupervisorDashboard />} />
                <Route path={Paths.TrainingPanel} element={<TrainingPanelDashboard isHome={true} />} />
                <Route path={Paths.TrainingIndex} element={<TrainingIndexDashboard isHome={true} />} />

                <Route path={Paths.Supervisor} element={<SupervisorWrapper />}>
                    <Route index element={<SupervisorTrainings initialMode={SupervisorTrainingsQueryMode.Instructor} />} />
                    <Route path={Paths.Instructor} element={<SupervisorTrainings initialMode={SupervisorTrainingsQueryMode.Instructor} />} />
                    <Route path={Paths.Coordinator} element={<SupervisorTrainings initialMode={SupervisorTrainingsQueryMode.Coordinator} />} />

                    <Route path=":classroomId" >
                        <Route index element={<SupervisorClassroom />} />
                        <Route path=":trainingType/rules" element={<SupervisorClassroomRules />} />
                        <Route path="notices" element={<SupervisorClassroomNotices />} />
                        <Route path="Meeting/:componentId" element={<RegisterMeeting />} />
                        <Route path="MeetingMatrix" element={<RegisterMeetingMatrix />} />
                        <Route path="Activity/:componentId" element={<SupervisorRegisterActivity />} />
                        <Route path="ActivityMatrix" element={<RegisterActivityMatrix />} />
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to={RootPaths.PageNotFound} />} />
            </Route>

            <Route element={<WelcomeLayout />}>
                <Route path={Paths.EnrollmentViewer}>
                    <Route path=":trainingType" element={<EnrollmentView />} />
                    <Route path=":trainingType/:enrollmentId" element={<EnrollmentView />} />
                </Route>

                <Route path={Paths.TrainingViewer}>
                    <Route path=":trainingId" element={<TrainingView />} />
                    <Route path=":trainingId/:classroomId" element={<TrainingView />} />
                </Route>

                <Route path={Paths.TrainingPreview}>
                    <Route path=":trainingId" element={<TrainingPreview />} />
                    <Route path=":trainingId/:classroomId" element={<TrainingPreview />} />
                </Route>

                <Route path={Paths.Approvals}>
                    <Route index element={<LmsApprovals />} />
                    <Route path={Paths.PreApprovals} element={<LmsApprovals initialMode="PreApprovals" />} />
                    <Route path={Paths.ConclusionForm} element={<LmsApprovals initialMode="ConclusionForm" />} />
                </Route>

                <Route path={Paths.Approvals} element={<LmsApprovals />} />

                <Route path={Paths.CarouselTrainingAttending} element={<LmsCarouselTrainingAttending />} />
                <Route path={Paths.CarouselTrainingMandatory} element={<LmsCarouselTrainingMandatory />} />

            </Route>

            <Route element={<BlankLayout />}>
                <Route path={Paths.Supervisor}>
                    <Route path=":classroomId/ClassroomSchedule" element={<SupervisorClassroomSchedule />} />
                    <Route path=":classroomId/MeetingAttendanceList/:componentId" element={<MeetingAttendanceList />} />
                    <Route path=":classroomId/MeetingQrCode/:componentId" element={<MeetingQrCode />} />
                </Route>
            </Route>
        </Routes>
    );
};
